/**
 * Workflow Service - UX Overhaul Sprint 2
 * Wire WorkflowProvider.tsx to real API
 * 
 * Story WF-1: WorkflowProvider context manages workflow state, syncs with API
 * NO MOCK DATA - Real API calls only
 */

import { apiFetch } from '@/lib/api';
import { getPrerequisites, isWorkflowStepNumber, type StepNumber } from '@/utils/workflow/prerequisiteRegistry';

// Types matching backend models
export type WorkflowStepType = 'user_input' | 'ai_generated' | 'user_approval';
export type WorkflowStepStatus = 'not_started' | 'in_progress' | 'completed' | 'needs_review' | 'blocked';

export interface WorkflowGate {
  id: string;
  name: string;
  required_steps: number[];
  passed: boolean;
  passed_at: string | null;
}

export interface WorkflowStep {
  step_number: number;
  name: string;
  type: WorkflowStepType;
  status: WorkflowStepStatus;
  primary_agent: string | null;
  estimated_duration_minutes: number;
  started_at: string | null;
  completed_at: string | null;
  data: Record<string, unknown> | null;
}

export interface WorkflowState {
  id: string;
  campaign_id: string;
  organization_id?: string;  // Campaign owner's org ID for auth
  current_step: number;
  steps: WorkflowStep[];
  gates: WorkflowGate[];
  started_at: string;
  last_activity: string;
  completed_at: string | null;
  is_paused: boolean;
}

export interface WorkflowCreateRequest {
  campaign_id: string;
  client_name?: string;
}

export interface WorkflowStepUpdateRequest {
  status?: WorkflowStepStatus;
  data?: Record<string, unknown>;
}

class WorkflowService {
  /**
   * Create new workflow
 * Story WF-2: New Campaign quick action launches workflow at Step 1
   */
  async createWorkflow(request: WorkflowCreateRequest): Promise<WorkflowState> {
    return await apiFetch<WorkflowState>('/api/v1/workflows', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  /**
   * Get workflow state by ID
   * Story WF-1: Context manages workflow state, syncs with API
   */
  async getWorkflow(workflowId: string): Promise<WorkflowState> {
    return await apiFetch<WorkflowState>(`/api/v1/workflows/${workflowId}`);
  }

  /**
   * Get workflow by campaign ID
   * Story WF-8: Resume workflow where user left off
   */
  async getWorkflowByCampaign(campaignId: string): Promise<WorkflowState | null> {
    return await apiFetch<WorkflowState | null>(
      `/api/v1/workflows/campaign/${campaignId}`
    );
  }

  /**
   * Update a workflow step
   * Story WF-4 through WF-7: Step-specific updates
   */
  async updateStep(
    workflowId: string, 
    stepNumber: number, 
    data: WorkflowStepUpdateRequest
  ): Promise<WorkflowState> {
    return await apiFetch<WorkflowState>(
      `/api/v1/workflows/${workflowId}/step/${stepNumber}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }
    );
  }

  /**
   * Complete a step and advance to next
   */
  async completeStep(
    workflowId: string, 
    stepNumber: number, 
    stepData?: Record<string, unknown>
  ): Promise<WorkflowState> {
    return this.updateStep(workflowId, stepNumber, {
      status: 'completed',
      data: stepData
    });
  }

  /**
   * Pause workflow
   */
  async pauseWorkflow(workflowId: string): Promise<{ status: string; workflow_id: string }> {
    return await apiFetch<{ status: string; workflow_id: string }>(
      `/api/v1/workflows/${workflowId}/pause`, { method: 'POST' }
    );
  }

  /**
   * Resume workflow
   */
  async resumeWorkflow(workflowId: string): Promise<{ status: string; workflow_id: string }> {
    return await apiFetch<{ status: string; workflow_id: string }>(
      `/api/v1/workflows/${workflowId}/resume`, { method: 'POST' }
    );
  }

  /**
   * Create workflow pre-populated with Entity Manager data.
   * Story 2.1: Entity Manager exit navigation fix.
   * No campaign_id required — campaign is created later in Step 4.
   */
  async createFromEntities(data: {
    organization_id?: string;
    client_id?: string;
    client_name?: string;
    partner_ids?: string[];
    vendor_ids?: string[];
    solution_ids?: string[];
    icp_ids?: string[];
  }): Promise<WorkflowState> {
    return await apiFetch<WorkflowState>('/api/v1/workflows/from-entities', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Save workflow as draft for later continuation
   * Story WF-8: Resume workflow where user left off
   */
  async saveDraft(
    workflowId: string, 
    stepData?: Record<string, unknown>,
    notes?: string
  ): Promise<{ workflow_id: string; saved_at: string; is_draft: boolean; draft_version: number; message: string }> {
    return await apiFetch<{ 
      workflow_id: string; 
      saved_at: string; 
      is_draft: boolean; 
      draft_version: number; 
      message: string;
    }>(`/api/v1/workflows/${workflowId}/draft`, {
      method: 'POST',
      body: JSON.stringify({ step_data: stepData, notes })
    });
  }

  /**
   * Get estimated time remaining for workflow
   */
  getEstimatedTimeRemaining(workflow: WorkflowState): number {
    let remaining = 0;
    for (const step of workflow.steps) {
      if (step.status === 'not_started' || step.status === 'in_progress') {
        remaining += step.estimated_duration_minutes;
      }
    }
    return remaining;
  }

  /**
   * Check if a step is accessible based on prerequisite completion.
   * Uses centralized frontend policy registry to keep WorkflowPage/sidebar/service aligned.
   */
  canAccessStep(workflow: WorkflowState, stepNumber: number): boolean {
    if (!isWorkflowStepNumber(stepNumber)) return false;
    if (stepNumber === 1) return true;

    const requiredSteps = getPrerequisites(stepNumber);
    if (requiredSteps.length === 0) return true;

    const completedStepNumbers = new Set<number>(
      workflow.steps.filter((step) => step.status === 'completed').map((step) => step.step_number),
    );
    return requiredSteps.every((requiredStep) => completedStepNumbers.has(requiredStep));
  }

  /**
   * Get the prerequisite step number needed for a given step.
   * Used for user-facing messages ("Complete Step N first").
   * Story 5.2: Sequential Step Enforcement
   */
  getPrerequisiteStep(stepNumber: number): number | null {
    if (!isWorkflowStepNumber(stepNumber) || stepNumber <= 1) return null;
    const prerequisites = getPrerequisites(stepNumber as StepNumber);
    return prerequisites.length > 0 ? prerequisites[prerequisites.length - 1] : null;
  }

  /**
   * Get missing prerequisite steps for a given step.
   * Returns array of step numbers that need completion.
   * Story 5.2: For Step 9, returns all incomplete from [6, 7, 8].
   */
  getMissingPrerequisites(workflow: WorkflowState, stepNumber: number): number[] {
    if (!isWorkflowStepNumber(stepNumber)) return [];
    const requiredSteps = getPrerequisites(stepNumber as StepNumber);
    if (requiredSteps.length === 0) return [];

    const completedStepNumbers = new Set<number>(
      workflow.steps.filter((step) => step.status === 'completed').map((step) => step.step_number),
    );

    return requiredSteps.filter((requiredStep) => !completedStepNumbers.has(requiredStep));
  }
}

export const workflowService = new WorkflowService();
export default workflowService;
